import type {BulkNotificationProfileSerializer} from "@/generated/DjangoTypes";
import {client} from "@/httpClient";
import {createFileRoute} from "@tanstack/react-router";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useTitle} from "react-use";

const NOTIFICATION_ACTION_TYPES = [
  "OPEN_CAMPAIGN",
  "OPEN_CHAT",
  "OPEN_TASK",
  "OPEN_REFLUENCED_TASK",
  "OPEN_INFLUENCER_RATINGS",
  "OPEN_DECLINED_VERIFICATION_SCREEN",
  "HIGHLIGHT",
  "REVIEW",
  "OPEN_CAMPAIGNS",
  "OPEN_RECOMMENDED_CAMPAIGNS",
  "OPEN_EVENTS",
  "OPEN_REFERRAL",
] as const;

export const Route = createFileRoute("/_authenticated/_admin/admins/bulk-notification")({
  component: BulkNotification,
});

function BulkNotification() {
  const {t} = useTranslation("navigation");

  useTitle(t("bulk_notifications"));

  const [actionType, setActionType] = useState("OPEN_CAMPAIGN");
  const [bodyDe, setBodyDe] = useState("");
  const [bodyEn, setBodyEn] = useState("");
  const [campaignId, setCampaignId] = useState<number | null>(null);
  const [companyId, setCompanyId] = useState("");
  const [failure, setFailure] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [profileName, setProfileName] = useState("");
  const [profiles, setProfiles] = useState<BulkNotificationProfileSerializer[]>([]);
  const [saveProfile, setSaveProfile] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState("custom");
  const [show, setShow] = useState(false);
  const [success, setSuccess] = useState(false);
  const [titleDe, setTitleDe] = useState("");
  const [titleEn, setTitleEn] = useState("");
  const [users, setUsers] = useState("");

  useEffect(() => {
    client.get(`/users/bulk_notification_profiles/`).then(response => {
      setProfiles(response.data);
    });
  }, []);
  useEffect(() => {
    if (selectedProfile !== "custom") {
      const profile = profiles.find(profile => profile.name === selectedProfile);
      setUsers(profile?.users!);
      setSelectAll(profile?.select_all!);
      setShow(profile?.show!);
      setTitleEn(profile?.title_en!);
      setBodyEn(profile?.body_en!);
      setTitleDe(profile?.title_de!);
      setBodyDe(profile?.body_de!);
      setActionType(profile?.action_type!);
      setCampaignId(profile?.campaign_id!);
      setImageUrl(profile?.image_url!);
    } else {
      setUsers("");
      setSelectAll(false);
      setShow(false);
      setTitleEn("");
      setBodyEn("");
      setTitleDe("");
      setBodyDe("");
      setActionType("OPEN_CAMPAIGN");
      setCampaignId(null);
      setCompanyId("");
      setImageUrl("");
    }
  }, [selectedProfile]);

  return (
    <div className="py-8 lg:pb-20">
      <div className="max-w-wide relative z-20 mx-auto px-5 md:px-8">
        <div className="flex">
          <div className="flex-1">
            <div className="mb-16 flex flex-col items-start rounded-3xl bg-white p-5 shadow-lg">
              <div>
                <h2 className="-mt-3 mb-2">Bulk Notifications</h2>
              </div>
              <div className="mb-4 w-full">
                <form className="-mx-2.5 flex flex-wrap">
                  <div className="mx-2.5 mb-6 flex basis-full flex-col flex-wrap gap-2">
                    <label
                      htmlFor="name-4"
                      className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                      Notification Profiles
                    </label>
                    <select
                      className="shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1"
                      value={selectedProfile}
                      onChange={event => setSelectedProfile(event.target.value)}>
                      <option value="custom" key={0}>
                        Custom
                      </option>
                      {profiles.map(profile => (
                        <option value={profile.name ?? ""} key={profile.id}>
                          {profile.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mx-2.5 mb-6 flex basis-full flex-col flex-wrap gap-2">
                    <label
                      htmlFor="name"
                      className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                      New line separated user ids
                    </label>
                    <textarea
                      className="shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1"
                      placeholder="New line separated user ids"
                      value={users}
                      onChange={event => setUsers(event.target.value)}
                    />
                  </div>
                  <div className="mx-2.5 mb-6 flex basis-full flex-col flex-wrap gap-2">
                    <label className="mb-1.5 block pl-5 text-lg leading-none font-medium">
                      <input
                        className="float-left mt-1 -ml-5"
                        checked={selectAll}
                        type="checkbox"
                        onChange={() => setSelectAll(!selectAll)}
                      />
                      <span>Select all users (If selected the above user ids will not be needed)</span>
                    </label>
                  </div>
                  <div className="mx-2.5 mb-2 flex basis-full flex-col flex-wrap gap-2">
                    <p className="text-good-grey-500 text-xs">
                      Fill in both languages to route by the user's language. Leave a language empty to send the other
                      language to everyone.
                    </p>
                  </div>
                  <div className="mx-2.5 mb-6 flex basis-full flex-col flex-wrap gap-2">
                    <label className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                      Title (English)
                    </label>
                    <input
                      type="text"
                      className="shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1"
                      placeholder="Title (English)"
                      value={titleEn}
                      onChange={event => setTitleEn(event.target.value)}
                    />
                  </div>
                  <div className="mx-2.5 mb-6 flex basis-full flex-col flex-wrap gap-2">
                    <label className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                      Body (English)
                    </label>
                    <textarea
                      className="shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1"
                      placeholder="Body (English)"
                      value={bodyEn}
                      onChange={event => setBodyEn(event.target.value)}
                    />
                  </div>
                  <div className="mx-2.5 mb-6 flex basis-full flex-col flex-wrap gap-2">
                    <label className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                      Title (German)
                    </label>
                    <input
                      type="text"
                      className="shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1"
                      placeholder="Title (German)"
                      value={titleDe}
                      onChange={event => setTitleDe(event.target.value)}
                    />
                  </div>
                  <div className="mx-2.5 mb-6 flex basis-full flex-col flex-wrap gap-2">
                    <label className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                      Body (German)
                    </label>
                    <textarea
                      className="shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1"
                      placeholder="Body (German)"
                      value={bodyDe}
                      onChange={event => setBodyDe(event.target.value)}
                    />
                  </div>
                  <div className="mx-2.5 mb-6 flex basis-full flex-col flex-wrap gap-2">
                    <label
                      htmlFor="name-4"
                      className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                      Notification Action Type
                    </label>
                    <select
                      className="shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1"
                      value={actionType}
                      onChange={event => setActionType(event.target.value)}>
                      {NOTIFICATION_ACTION_TYPES.map(notification => (
                        <option value={notification} key={notification}>
                          {notification}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mx-2.5 mb-6 flex basis-full flex-col flex-wrap gap-2">
                    <label className="mb-1.5 block pl-5 text-lg leading-none font-medium">
                      <input
                        className="float-left mt-1 -ml-5"
                        checked={show}
                        type="checkbox"
                        onChange={() => setShow(!show)}
                      />
                      <span>Should this notification show up in the notification center of the users in the app?</span>
                    </label>
                  </div>
                  <div className="mx-2.5 mb-6 flex grow flex-col flex-wrap">
                    <label
                      htmlFor="name-4"
                      className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                      Campaign id for actions params
                    </label>
                    <input
                      type="number"
                      className="shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1"
                      placeholder="Campaign id"
                      value={campaignId!}
                      onChange={event => setCampaignId(+event.target.value)}
                    />
                  </div>
                  <div className="mx-2.5 mb-6 flex grow flex-col flex-wrap">
                    <label
                      htmlFor="name-4"
                      className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                      Company id for actions params
                    </label>
                    <input
                      type="number"
                      className="shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1"
                      placeholder="Company id"
                      value={companyId}
                      onChange={event => setCompanyId(event.target.value)}
                    />
                  </div>
                  <div className="mx-2.5 mb-6 flex basis-full flex-col flex-wrap md:basis-2/5">
                    <label
                      htmlFor="name-4"
                      className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                      Image url
                    </label>
                    <input
                      type="text"
                      className="shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1"
                      placeholder="Image url"
                      value={imageUrl}
                      onChange={event => setImageUrl(event.target.value)}
                    />
                  </div>
                  <div className="mx-2.5 mb-6 flex basis-full flex-col flex-wrap">
                    <label className="mb-1.5 block pl-5 text-lg leading-none font-medium">
                      <input
                        className="float-left mt-1 -ml-5"
                        checked={saveProfile}
                        type="checkbox"
                        onChange={() => setSaveProfile(!saveProfile)}
                      />
                      <span>Save profile</span>
                    </label>
                  </div>
                  <div className="mx-2.5 mb-6 flex basis-full flex-col flex-wrap gap-2">
                    <label
                      htmlFor="name-4"
                      className="text-good-grey-500 mt-4 block text-xs font-semibold tracking-wider uppercase">
                      Profile name
                    </label>
                    <input
                      type="text"
                      className="shadow-highlight focus:outline-good-grey-100 mb-1.5 block w-full resize-y rounded-2xl bg-white p-2 text-gray-500 focus:shadow-none focus:outline-2 focus:-outline-offset-1"
                      placeholder="Save these notification settings in a named profile (default = title)"
                      value={profileName}
                      onChange={event => setProfileName(event.target.value)}
                    />
                  </div>
                </form>
                {success && (
                  <div>
                    <div>Bulk push notifications sent successfully!</div>
                  </div>
                )}
                {failure && (
                  <div>
                    <div>Bulk push notifications could not be sent!</div>
                  </div>
                )}
              </div>
              <button
                className="bg-bold-blue-500 hover:shadow-highlight mr-2.5 min-h-10 min-w-10 cursor-pointer rounded-2xl border-none px-5 py-3 text-center font-medium tracking-wider text-white uppercase hover:scale-105"
                onClick={event => {
                  event.preventDefault();
                  client
                    .post(`/users/bulk_notification/`, {
                      users: users.trimEnd(),
                      select_all: selectAll,
                      show: show,
                      title_en: titleEn,
                      body_en: bodyEn,
                      title_de: titleDe,
                      body_de: bodyDe,
                      action_type: actionType,
                      campaign_id: campaignId,
                      company_id: companyId,
                      image_url: imageUrl,
                      save_profile: saveProfile,
                      profile_name: profileName,
                    })
                    .then(() => {
                      setSuccess(true);
                      setFailure(false);
                    })
                    .catch(() => {
                      setSuccess(false);
                      setFailure(true);
                    });
                }}>
                Send bulk push notifications
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
